import { Button } from "@/components/ui/button";
import { getPlatformMark } from "@mairko/brand";
import { IntegrationCard } from "./IntegrationCard.js";
import {
	CHANNEL_CATEGORY_ORDER,
	COMMS_CHANNELS,
	CONNECTOR_CATEGORY_ORDER,
	CONNECTORS,
	INTEGRATION_CATEGORY_LABEL,
	INTEGRATION_CATEGORY_LEDE,
	type Integration,
	type IntegrationCategory,
	type IntegrationStatus,
} from "./nav.js";
import {
	ArrowRightIcon,
	CheckIcon,
	LayoutGridIcon,
	ListIcon,
} from "lucide-react";

export type IntegrationsView = "grid" | "list";
export type IntegrationsFilter = "all" | "available" | "soon" | "connected";

/** @deprecated Prefer IntegrationsView — kept for App imports. */
export type ConnectorsView = IntegrationsView;
/** @deprecated Prefer IntegrationsFilter — kept for App imports. */
export type ConnectorsFilter = IntegrationsFilter;

export type ChannelsView = IntegrationsView;
export type ChannelsFilter = IntegrationsFilter;

type CatalogKind = "connector" | "channel";

type CatalogCopy = {
	title: string;
	noun: string;
	nounPlural: string;
	filterAria: string;
	layoutAria: string;
	empty: string;
	availableVerb: string;
	soonVerb: string;
};

const COPY: Record<CatalogKind, CatalogCopy> = {
	connector: {
		title: "Connectors",
		noun: "connector",
		nounPlural: "connectors",
		filterAria: "Filter connectors",
		layoutAria: "Catalog layout",
		empty: "No connectors in this filter.",
		availableVerb: "Connect",
		soonVerb: "Preview",
	},
	channel: {
		title: "Channels",
		noun: "channel",
		nounPlural: "channels",
		filterAria: "Filter channels",
		layoutAria: "Channel layout",
		empty: "No channels in this filter.",
		availableVerb: "Bind",
		soonVerb: "Preview",
	},
};

const STATUS_RANK: Record<IntegrationStatus, number> = {
	attention: 0,
	connected: 1,
	available: 2,
	soon: 3,
};

function matchesFilter(
	item: Integration,
	filter: IntegrationsFilter,
): boolean {
	switch (filter) {
		case "all":
			return true;
		case "available":
			return item.status === "available" || item.status === "attention";
		case "soon":
			return item.status === "soon";
		case "connected":
			return item.status === "connected";
		default: {
			const _exhaustive: never = filter;
			return _exhaustive;
		}
	}
}

function statusLabel(status: IntegrationStatus): string {
	switch (status) {
		case "connected":
			return "Connected";
		case "available":
			return "Available";
		case "soon":
			return "Soon";
		case "attention":
			return "Needs attention";
		default: {
			const _exhaustive: never = status;
			return _exhaustive;
		}
	}
}

function sortCatalogItems(items: Integration[]): Integration[] {
	return [...items].sort((a, b) => {
		const byStatus = STATUS_RANK[a.status] - STATUS_RANK[b.status];
		if (byStatus !== 0) return byStatus;
		return a.name.localeCompare(b.name);
	});
}

function groupByCategory(
	items: readonly Integration[],
	order: readonly IntegrationCategory[],
): Array<{ category: IntegrationCategory; items: Integration[] }> {
	const groups: Array<{ category: IntegrationCategory; items: Integration[] }> =
		[];
	for (const category of order) {
		const inCat = items.filter((item) => item.category === category);
		if (inCat.length > 0) {
			groups.push({ category, items: sortCatalogItems(inCat) });
		}
	}
	return groups;
}

function actionLabel(
	kind: CatalogKind,
	status: IntegrationStatus,
): string {
	const copy = COPY[kind];
	if (status === "connected") return "Manage";
	if (status === "soon") return copy.soonVerb;
	return copy.availableVerb;
}

function scrollToCategory(idPrefix: string, category: IntegrationCategory) {
	const heading = document.getElementById(`${idPrefix}-cat-${category}`);
	const section = heading?.closest("section");
	(section ?? heading)?.scrollIntoView({ behavior: "smooth", block: "start" });
}

type HubProps = {
	kind: CatalogKind;
	items: readonly Integration[];
	categoryOrder: readonly IntegrationCategory[];
	view: IntegrationsView;
	filter: IntegrationsFilter;
	onViewChange: (view: IntegrationsView) => void;
	onFilterChange: (filter: IntegrationsFilter) => void;
	onOpen: (item: Integration) => void;
};

function IntegrationsHub({
	kind,
	items,
	categoryOrder,
	view,
	filter,
	onViewChange,
	onFilterChange,
	onOpen,
}: HubProps) {
	const copy = COPY[kind];
	const availableCount = items.filter(
		(c) => c.status === "available" || c.status === "attention",
	).length;
	const soonCount = items.filter((c) => c.status === "soon").length;
	const connectedCount = items.filter((c) => c.status === "connected").length;
	const visible = items.filter((c) => matchesFilter(c, filter));
	const groups = groupByCategory(visible, categoryOrder);
	const idPrefix = kind === "connector" ? "conn" : "chan";

	return (
		<>
			<header className="main-header agents-hub-header">
				<div className="min-w-0">
					<h1 className="main-title">{copy.title}</h1>
					<p className="main-lede">
						{kind === "connector"
							? "Link the platforms your team uses. Your credentials stay private."
							: "Choose where approval requests reach your team."}
					</p>
				</div>
				<div
					className="agents-view-toggle"
					role="group"
					aria-label={copy.layoutAria}
				>
					<button
						type="button"
						className="agents-view-btn"
						aria-pressed={view === "grid"}
						onClick={() => onViewChange("grid")}
						title="Grid view"
					>
						<LayoutGridIcon className="size-4" />
						<span>Grid</span>
					</button>
					<button
						type="button"
						className="agents-view-btn"
						aria-pressed={view === "list"}
						onClick={() => onViewChange("list")}
						title="List view"
					>
						<ListIcon className="size-4" />
						<span>List</span>
					</button>
				</div>
			</header>

			<div className="integrations agents-hub">
				<div className="agents-hub-toolbar">
					<div
						className="agents-filter"
						role="group"
						aria-label={copy.filterAria}
					>
						{(
							[
								{ id: "all" as const, label: "All", count: items.length },
								{
									id: "available" as const,
									label: "Available",
									count: availableCount,
								},
								{ id: "soon" as const, label: "Soon", count: soonCount },
								...(connectedCount > 0
									? [
											{
												id: "connected" as const,
												label: "Connected",
												count: connectedCount,
											},
										]
									: []),
							] satisfies Array<{
								id: IntegrationsFilter;
								label: string;
								count: number;
							}>
						).map(({ id, label, count }) => (
							<button
								key={id}
								type="button"
								className="agents-filter-btn"
								aria-pressed={filter === id}
								onClick={() => onFilterChange(id)}
							>
								{label}
								<span className="agents-filter-count">{count}</span>
							</button>
						))}
					</div>
					<p className="agents-hub-count">
						{visible.length}{" "}
						{visible.length === 1 ? copy.noun : copy.nounPlural}
					</p>
				</div>

				{groups.length > 1 && (
					<nav
						className="connectors-cat-nav"
						aria-label={`${copy.title} categories`}
					>
						{groups.map(({ category, items: groupItems }) => (
							<button
								key={category}
								type="button"
								className="connectors-cat-chip"
								onClick={() => scrollToCategory(idPrefix, category)}
							>
								<span className="connectors-cat-chip-label">
									{INTEGRATION_CATEGORY_LABEL[category]}
								</span>
								<span className="connectors-cat-chip-meta">
									{groupItems.length}
								</span>
							</button>
						))}
					</nav>
				)}

				{visible.length === 0 ? (
					<p className="agents-hub-empty">{copy.empty}</p>
				) : (
					<div className="connectors-groups">
						{groups.map(({ category, items: groupItems }) => (
							<section
								key={category}
								className="connectors-group"
								aria-labelledby={`${idPrefix}-cat-${category}`}
							>
								<div className="connectors-group-head">
									<div className="connectors-group-copy">
										<h2
											id={`${idPrefix}-cat-${category}`}
											className="connectors-group-title"
										>
											{INTEGRATION_CATEGORY_LABEL[category]}
										</h2>
										<p className="connectors-group-lede">
											{INTEGRATION_CATEGORY_LEDE[category]}
										</p>
									</div>
									<span className="connectors-group-count">
										{groupItems.length}
									</span>
								</div>
								{view === "grid" ? (
									<div className="connectors-grid">
										{groupItems.map((item) => (
											<IntegrationCard
												key={item.id}
												item={item}
												actionLabel={actionLabel(kind, item.status)}
												onAction={() => onOpen(item)}
											/>
										))}
									</div>
								) : (
									<ul className="connectors-list">
										{groupItems.map((item) => (
											<CatalogListRow
												key={item.id}
												item={item}
												action={actionLabel(kind, item.status)}
												onOpen={() => onOpen(item)}
											/>
										))}
									</ul>
								)}
							</section>
						))}
					</div>
				)}
			</div>
		</>
	);
}

function CatalogListRow({
	item,
	action,
	onOpen,
}: {
	item: Integration;
	action: string;
	onOpen: () => void;
}) {
	const mark = getPlatformMark(item.markId, 28);

	return (
		<li
			className={
				item.status === "connected"
					? "connectors-list-row connectors-list-row--connected"
					: item.status === "soon"
						? "connectors-list-row connectors-list-row--soon"
						: "connectors-list-row"
			}
		>
			<button
				type="button"
				className="connectors-list-main"
				onClick={onOpen}
				aria-label={`Open ${item.name}`}
			>
				<span className="connectors-list-logo" aria-hidden>
					{mark ? (
						<span
							className="integration-mark"
							dangerouslySetInnerHTML={{ __html: mark.svg }}
						/>
					) : (
						<span className="integration-fallback">
							{item.name.slice(0, 1)}
						</span>
					)}
				</span>
				<span className="connectors-list-copy">
					<span className="connectors-list-name">{item.name}</span>
					<span className="connectors-list-blurb">{item.blurb}</span>
				</span>
				<span className="connectors-list-meta">
					{item.status === "connected" ? (
						<span className="connectors-list-connected">
							<CheckIcon className="size-3.5" />
							Connected
						</span>
					) : item.status === "attention" ? (
						<span className="connectors-list-attention">Needs attention</span>
					) : item.status === "soon" ? (
						<span className="connectors-list-status">Soon</span>
					) : (
						<span className="connectors-list-status">
							{statusLabel(item.status)}
						</span>
					)}
				</span>
			</button>
			<span className="connectors-list-actions">
				<Button
					type="button"
					variant="ghost"
					size="sm"
					className="h-8 gap-1.5"
					onClick={onOpen}
				>
					{action}
					<ArrowRightIcon className="size-3.5" />
				</Button>
			</span>
		</li>
	);
}

type ShellProps = {
	view: IntegrationsView;
	filter: IntegrationsFilter;
	onViewChange: (view: IntegrationsView) => void;
	onFilterChange: (filter: IntegrationsFilter) => void;
	onOpen: (item: Integration) => void;
	/** Live-merged catalog from platform connections; falls back to static. */
	items?: readonly Integration[];
};

/** Connectors desk hub — categorized catalog with grid/list + status filters. */
export function ConnectorsHub({ items, ...props }: ShellProps) {
	return (
		<IntegrationsHub
			kind="connector"
			items={items ?? CONNECTORS}
			categoryOrder={CONNECTOR_CATEGORY_ORDER}
			{...props}
		/>
	);
}

/** Channels desk hub — messaging & task binds for approvals. */
export function ChannelsHub({ items, ...props }: ShellProps) {
	return (
		<IntegrationsHub
			kind="channel"
			items={items ?? COMMS_CHANNELS}
			categoryOrder={CHANNEL_CATEGORY_ORDER}
			{...props}
		/>
	);
}
